/**
 * Measurement primitives ported from the legacy WebViewerActiveX
 * uChiroToolsWizard1.cpp. All math operates on world (mm) coordinates
 * captured from viewport.canvasToWorld — so distances are in mm and
 * angles in degrees straight out of these functions.
 *
 * Legacy call sites for reference:
 *   uChiroToolsWizard1.cpp:296  CalculateAngle(x1,y1, x2,y2, x3,y3)
 *   uChiroToolsWizard1.cpp:324  CalculateAngle(x1,y1, x2,y2, x3,y3, x4,y4)
 *   uChiroToolsWizard1.cpp:471  PointToLinePerpendicular
 */
import type { ChiroPoint } from './types';

/** Straight distance between two points, in whatever unit the coords use. */
export function distance(a: ChiroPoint, b: ChiroPoint): number {
  const dx = a.worldX - b.worldX;
  const dy = a.worldY - b.worldY;
  const dz = a.worldZ - b.worldZ;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** Vertex angle at B, measured between rays BA and BC. Returns degrees in [0, 180]. */
export function angle3Point(a: ChiroPoint, b: ChiroPoint, c: ChiroPoint): number {
  const ux = a.worldX - b.worldX;
  const uy = a.worldY - b.worldY;
  const vx = c.worldX - b.worldX;
  const vy = c.worldY - b.worldY;
  const dot = ux * vx + uy * vy;
  const magU = Math.hypot(ux, uy);
  const magV = Math.hypot(vx, vy);
  if (magU === 0 || magV === 0) return 0;
  const cos = Math.max(-1, Math.min(1, dot / (magU * magV)));
  return (Math.acos(cos) * 180) / Math.PI;
}

/**
 * Angle between line1(a→b) and line2(c→d). Returns degrees in [0, 180].
 * This is the Cobb-angle primitive: two lines drawn along superior/inferior
 * end-plates, the angle between them is the scoliosis measurement.
 */
export function angle4Point(a: ChiroPoint, b: ChiroPoint, c: ChiroPoint, d: ChiroPoint): number {
  const t1 = Math.atan2(b.worldY - a.worldY, b.worldX - a.worldX);
  const t2 = Math.atan2(d.worldY - c.worldY, d.worldX - c.worldX);
  let deg = Math.abs(((t1 - t2) * 180) / Math.PI);
  if (deg > 180) deg = 360 - deg;
  if (deg > 90) deg = 180 - deg;
  return deg;
}

/** Perpendicular distance from point P to the line through A and B. */
export function pointToLineDistance(p: ChiroPoint, a: ChiroPoint, b: ChiroPoint): number {
  const dx = b.worldX - a.worldX;
  const dy = b.worldY - a.worldY;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return distance(p, a);
  const t = ((p.worldX - a.worldX) * dx + (p.worldY - a.worldY) * dy) / len2;
  const projX = a.worldX + t * dx;
  const projY = a.worldY + t * dy;
  const ex = p.worldX - projX;
  const ey = p.worldY - projY;
  return Math.hypot(ex, ey);
}

/** Foot of perpendicular from P onto line AB. Useful for drawing drop lines. */
export function projectOntoLine(p: ChiroPoint, a: ChiroPoint, b: ChiroPoint): ChiroPoint {
  const dx = b.worldX - a.worldX;
  const dy = b.worldY - a.worldY;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return { ...a };
  const t = ((p.worldX - a.worldX) * dx + (p.worldY - a.worldY) * dy) / len2;
  return {
    worldX: a.worldX + t * dx,
    worldY: a.worldY + t * dy,
    worldZ: a.worldZ,
  };
}

/** Extend the line A→B past both ends by `factor` of its own length. */
export function extendLine(
  a: ChiroPoint,
  b: ChiroPoint,
  factor: number,
): { a: ChiroPoint; b: ChiroPoint } {
  const dx = b.worldX - a.worldX;
  const dy = b.worldY - a.worldY;
  return {
    a: { worldX: a.worldX - dx * factor, worldY: a.worldY - dy * factor, worldZ: a.worldZ },
    b: { worldX: b.worldX + dx * factor, worldY: b.worldY + dy * factor, worldZ: b.worldZ },
  };
}

/** Midpoint of two points (used for placing labels between lines). */
export function midpoint(a: ChiroPoint, b: ChiroPoint): ChiroPoint {
  return {
    worldX: (a.worldX + b.worldX) / 2,
    worldY: (a.worldY + b.worldY) / 2,
    worldZ: (a.worldZ + b.worldZ) / 2,
  };
}
